import { AIAppIcon } from '@/assets/AIAppIcon';
import { StepConfig } from './multi-step-form.model';
import { DeveloperIcon } from '@/assets/Developer';
import { LaptopDevConfigIcon } from '@/assets/LaptopDevConfig';
import { QuestionIcon } from '@/assets/Question';
import { SearchBoxIcon } from '@/assets/SearchBox';
import { UploadDataWindowIcon } from '@/assets/UploadDataWindow';
import { CloudDatabaseTreeIcon } from '@/assets/CloudDatabaseTree';
import { LaptopDevModeIcon } from '@/assets/LaptopDevMode';
import { DeviceMobileIcon } from '@/assets/DeviceMobile';
import { UxCircleLineIcon } from '@/assets/UxCircleLine';
import { MultiSystemsArrows } from '@/assets/MultiSystemsArrows';
import { PlatformMigrationIcon } from '@/assets/PlatformMigrationIcon';
export interface CategoryType {
  STEP_1: string;
  STEP_2: string;
}

export const IntakeFormId = {
  'digital-product-engineering': {
    STEP_1: `${process.env.NEXT_PUBLIC_HS_GUID_ENGINEERING_FORM_STEP_1}`,
    STEP_2: `${process.env.NEXT_PUBLIC_HS_GUID_ENGINEERING_FORM_STEP_2}`,
  },
  'application-modernization': {
    STEP_1: `${process.env.NEXT_PUBLIC_HS_GUID_ENTERPRISE_FORM_STEP_1}`,
    STEP_2: `${process.env.NEXT_PUBLIC_HS_GUID_ENTERPRISE_FORM_STEP_2}`,
  },
  'embedded-engineering-teams': {
    STEP_1: `${process.env.NEXT_PUBLIC_HS_GUID_AUGMENTATION_FORM_STEP_1}`,
    STEP_2: `${process.env.NEXT_PUBLIC_HS_GUID_AUGMENTATION_FORM_STEP_2}`,
  },
} as const;

export const emailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const invalidEmailDomains = [
  'gmail.com',
  'yahoo.com',
  'outlook.com',
  'hotmail.com',
  'aol.com',
  'icloud.com',
  'protonmail.com',
  'mail.com',
  'yandex.com',
  'live.com',
];

export const BUDGET_VALUE_MAPPER = {
  '0': 'Less than $5k',
  '33.33': '$5k to $7k',
  '66.66': '$8k to $9k',
  '99.99': '$10k+',
} as const;

export const fieldsWithDynamicOtherInput = [
  'requested_services',
  'roles',
  'skills',
];
export const digitalProductEngineeringStepsConfig: StepConfig[] = [
  {
    eyebrow: 'Digital Product Engineering',
    title: "Step 1/2: Let's get your product started!",
    content:
      "Tell us about what you're building and where you need support. We'll use your details to understand your goals and connect you with the right Devsu team.",
    fields: [
      {
        name: 'firstname',
        label: 'First Name',
        required: true,
        type: 'text',
        placeholder: 'Enter your first name',
      },
      {
        name: 'lastname',
        label: 'Last Name',
        required: true,
        type: 'text',
        placeholder: 'Enter your last name',
      },
      {
        name: 'email',
        label: 'Email',
        type: 'email',
        required: true,
        placeholder: 'Enter your work email',
      },
      {
        name: 'company',
        label: 'Company Name',
        type: 'text',
        required: true,
        placeholder: "Name of the company you're hiring for",
      },
      {
        name: 'phone',
        label: 'Phone Number (optional)',
        type: 'tel',
        required: false,
        placeholder: 'Optional, but speeds up communication',
      },
    ],
  },
  {
    eyebrow: 'Digital Product Engineering',
    title: 'Step 2/2: Tell us about your needs',
    content:
      "Tell us about what you're building and where you need support. We'll use your details to understand your goals and connect you with the right Devsu team.",
    fields: [
      {
        name: 'requested_services',
        label: 'What software delivery approach are you interested in?',
        type: 'card',
        required: true,
        textInputPlaceHolder: 'Describe the services you need',
        options: [
          {
            label: 'Custom Software Development',
            value: 'custom-software-development',
            icon: <LaptopDevConfigIcon />,
          },
          {
            label: 'Web & Mobile App Development',
            value: 'web-mobile-app-development',
            icon: <UploadDataWindowIcon />,
          },
          {
            label: 'Managed Services & Support',
            value: 'managed-services-support',
            icon: <SearchBoxIcon />,
          },
          {
            label: 'DevOps & Automation',
            value: 'devops-automation',
            icon: <DeveloperIcon />,
          },
          {
            label: 'Other',
            value: 'Other',
            icon: <QuestionIcon />,
          },
        ],
      },
      {
        name: 'additional_project_info',
        label: 'Additional Information',
        required: false,
        type: 'textarea',
        placeholder:
          'Share any specific goals, challenges or details about your project.',
      },
      {
        name: 'additional_files',
        label: 'Upload documentation (optional)',
        required: false,
        type: 'file',
      },
    ],
  },
];

export const appModernizationStepsConfig: StepConfig[] = [
  {
    eyebrow: 'Application Modernization',
    title: "Step 1/2: Let's get your modernization started!",
    content:
      "Tell us about your system and modernization goals. We'll use your details to understand your environment and connect you with the right Devsu team.",
    fields: [
      {
        name: 'firstname',
        label: 'First Name',
        required: true,
        type: 'text',
        placeholder: 'Enter your first name',
      },
      {
        name: 'lastname',
        label: 'Last Name',
        required: true,
        type: 'text',
        placeholder: 'Enter your last name',
      },
      {
        name: 'email',
        label: 'Email',
        type: 'email',
        required: true,
        placeholder: 'Enter your work email',
      },
      {
        name: 'company',
        label: 'Company Name',
        type: 'text',
        required: true,
        placeholder: "Name of the company you're hiring for",
      },
      {
        name: 'phone',
        label: 'Phone Number (optional)',
        type: 'tel',
        required: false,
        placeholder: 'Optional, but speeds up communication',
      },
    ],
  },
  {
    eyebrow: 'Application Modernization',
    title: 'Step 2/2: Tell us about your needs',
    content:
      "Tell us about your system and modernization goals. We'll use your details to understand your environment and connect you with the right Devsu team.",
    fields: [
      {
        name: 'requested_services',
        label:
          'What type of application modernization project do you need our help with?',
        type: 'card',
        required: true,
        textInputPlaceHolder: 'Describe the services you need',
        options: [
          {
            label: 'Integrating AI & Automation into an Application',
            value: 'integrating-ai-automation',
            icon: <AIAppIcon />,
          },
          {
            label: 'Consolidating or Merging Multiple Systems',
            value: 'Consolidating or Merging Multiple Systems',
            icon: <MultiSystemsArrows />,
          },
          {
            label: 'Migrating a Legacy System to the Cloud',
            value: 'migrating-legacy-system',
            icon: <UploadDataWindowIcon />,
          },
          {
            label: 'Replatforming an Existing Application',
            value: 'replatforming-existing-application',
            icon: <PlatformMigrationIcon />,
          },
          {
            label: 'Other',
            value: 'Other',
            icon: <QuestionIcon />,
          },
        ],
      },
      {
        name: 'additional_project_info',
        label:
          'Is there any other relevant information you want to share about your project?',
        required: false,
        type: 'textarea',
        placeholder:
          'Share any specific goals, challenges or details about your project.',
      },
      {
        name: 'additional_files',
        label: 'Upload documentation (optional)',
        required: false,
        type: 'file',
      },
    ],
  },
];

export const embeddedEngineeringTeamsStepsConfig: StepConfig[] = [
  {
    eyebrow: 'Embedded Engineering Teams',
    title: "Step 1/2: Let's get your team started!",
    content:
      "Tell us about your engineering needs and the capacity you're looking to add. We'll use your details to understand your requirements and connect you with the right Devsu team.",
    fields: [
      {
        name: 'firstname',
        label: 'First Name',
        required: true,
        type: 'text',
        placeholder: 'Enter your first name',
      },
      {
        name: 'lastname',
        label: 'Last Name',
        required: true,
        type: 'text',
        placeholder: 'Enter your last name',
      },
      {
        name: 'email',
        label: 'Email',
        type: 'email',
        required: true,
        placeholder: 'Enter your work email',
      },
      {
        name: 'company',
        label: 'Company Name',
        type: 'text',
        required: true,
        placeholder: "Name of the company you're hiring for",
      },
      {
        name: 'phone',
        label: 'Phone Number (optional)',
        type: 'tel',
        required: false,
        placeholder: 'Optional, but speeds up communication',
      },
    ],
  },
  {
    eyebrow: 'Embedded Engineering Teams',
    title: 'Step 2/2: Fine tune your requirements',
    content:
      "Tell us about your engineering needs and the capacity you're looking to add. We'll use your details to understand your requirements and connect you with the right Devsu team.",
    fields: [
      {
        name: 'roles',
        label: 'Which role do you need to fill?',
        type: 'card',
        required: true,
        textInputPlaceHolder: 'Describe the roles you need',
        options: [
          {
            label: 'Backend Developer',
            value: 'backend-developer',
            icon: <LaptopDevConfigIcon />,
          },
          {
            label: 'Frontend Developer',
            value: 'frontend-developer',
            icon: <UploadDataWindowIcon />,
          },
          {
            label: 'Full-Stack Developer',
            value: 'full-stack-developer',
            icon: <LaptopDevModeIcon />,
          },
          {
            label: 'Mobile Developer',
            value: 'mobile-developer',
            icon: <DeviceMobileIcon />,
          },
          {
            label: 'DevOps Engineer',
            value: 'devops-engineer',
            icon: <DeveloperIcon />,
          },
          {
            label: 'Product Designer',
            value: 'product-designer',
            icon: <UxCircleLineIcon />,
          },
          {
            label: 'AI / Data Engineer',
            value: 'data-scientist',
            icon: <CloudDatabaseTreeIcon />,
          },
          {
            label: 'QA Engineer',
            value: 'qa-engineer',
            icon: <SearchBoxIcon />,
          },
          {
            label: 'Other',
            value: 'Other',
            icon: <QuestionIcon />,
          },
        ],
      },
      {
        name: 'professionals',
        label: 'How many professionals do you need?',
        type: 'range',
        required: true,
      },

      {
        name: 'seniority',
        label: 'What level of seniority do you require for these roles?',
        type: 'checkbox',
        required: true,
        options: [
          {
            label: 'Junior (1-3 years of experience)',
            value: 'Junior (1-3 years of experience)',
          },
          {
            label: 'Mid-Level (3-5 years of experience)',
            value: 'Mid-Level (3-5 years of experience)',
          },
          {
            label: 'Senior (5+ years of experience)',
            value: 'Senior (5+ years of experience)',
          },
          {
            label: 'Lead/Managerial',
            value: 'Lead/Managerial',
          },
          {
            label: 'Any level is fine',
            value: 'Any level is fine',
          },
        ],
        columns: 1,
      },
      {
        name: 'skills',
        label: 'What technologies or skills should they have?',
        type: 'checkbox',
        required: true,
        textInputPlaceHolder: 'Describe the skills you need',
        options: [
          { label: 'Node.js', value: 'Node.js' },
          { label: 'Python', value: 'Python' },
          {
            label: 'Java',
            value: 'Java',
          },
          {
            label: 'Ruby on Rails',
            value: 'Ruby on Rails',
          },
          {
            label: 'SQL',
            value: 'SQL',
          },
          {
            label: 'Docker',
            value: 'Docker',
          },
          {
            label: 'Other',
            value: 'Other',
          },
        ],
        columns: 2,
      },
    ],
  },
];

export const digitalProductEngineeringFormStepLabels = [
  `Let's get started!`,
  'Specify your needs',
];
export const appModernizationFormStepLabels = [
  `Let's get started!`,
  'Specify your needs',
];
export const embeddedEngineeringTeamsFormStepLabels = [
  `Set up account`,
  'Specify requirements',
];
